/**
 * The individual rows of the Filter Shipments dialog. Each owns one filter
 * field (or a matched pair, like a min/max price) and reports changes up; the
 * dialog owns the draft filter and the Apply.
 *
 * Every row is a single `FormItem`, because the dialog lays each tab out with
 * `FormGroup labelPosition="beside"` — a two-column label/control grid that a
 * row nesting its own `Row` of FormItems would break out of, leaving that one
 * field out of line with the rest of the tab.
 *
 * Filters that are already a single labelled control — carrier, shipment
 * state, province, date range — are rendered straight from their own component
 * by the dialog; there's nothing left for a wrapper here to add.
 */

import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Switch } from "@freightsimple/ui-core/Controls/Switch";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";

interface PriceRowProps {
  minPrice: number | undefined;
  maxPrice: number | undefined;
  onMinPriceChange: (newValue: number | undefined) => void;
  onMaxPriceChange: (newValue: number | undefined) => void;
}

interface PriceFieldProps {
  placeholder: string;
  value: number | undefined;
  onChange: (newValue: number | undefined) => void;
}

// A dollar amount filter field. Uncontrolled so partial input ("12.") keeps
// its text while the parsed number flows to the filter state.
function PriceField_(props: PriceFieldProps) {
  return (
    <TextInput
      type="text"
      placeholder={props.placeholder}
      defaultValue={props.value === undefined ? "" : String(props.value)}
      onChange={(e) => {
        const parsed = Number.parseFloat(e.target.value);
        props.onChange(Number.isFinite(parsed) ? parsed : undefined);
      }}
      width="full"
    />
  );
}

// Min and max share one "Price" label and read as a range either side of "to".
// Separately labelled they took two rows of the tab and still left the reader
// to work out that they were a pair.
export function PriceRow(props: PriceRowProps) {
  const { minPrice, maxPrice, onMaxPriceChange, onMinPriceChange } = props;
  return (
    <FormItem label="Price" fillWidth>
      <Row gap={2} align="center">
        <PriceField_
          placeholder="0"
          value={minPrice}
          onChange={onMinPriceChange}
        />
        <T size="sm" color="muted">
          to
        </T>
        <PriceField_
          placeholder="10,000+"
          value={maxPrice}
          onChange={onMaxPriceChange}
        />
      </Row>
    </FormItem>
  );
}

interface FilterRowProps {
  title: string;
  value: string | undefined;
  placeholder: string;
  onChange: (newValue: string | undefined) => void;
}

export function FilterRow(props: FilterRowProps) {
  return (
    <FormItem label={props.title} fillWidth>
      <TextInput
        type="text"
        width="full"
        allowClear
        value={props.value}
        placeholder={props.placeholder}
        onChange={(e) => {
          props.onChange(e.target.value === "" ? undefined : e.target.value);
        }}
      />
    </FormItem>
  );
}

interface FreightClaimsRowProps {
  onlyShowClaims: boolean;
  onHasClaimChange: (value: boolean) => void;
}

// The explanation sits beside the switch, not in FormItem's `description`: the
// label column is right-aligned in a beside-layout FormGroup, so a description
// under the label wraps into a ragged right-aligned block pointing away from
// the control it describes.
export function FreightClaimsRow(props: FreightClaimsRowProps) {
  return (
    <FormItem label="Freight Claims" fillWidth>
      <Row gap={3} align="center">
        <Switch
          checked={props.onlyShowClaims}
          onChange={props.onHasClaimChange}
        />
        <T size="sm" color={props.onlyShowClaims ? "secondary" : "muted"}>
          Only Shipments with Freight Claims
        </T>
      </Row>
    </FormItem>
  );
}
